import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../config/firebase';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  updateProfile,
} from 'firebase/auth';

const CreateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const paymentInfo = location.state as {
    paymentMethodId: string;
    email: string;
    name: string;
    priceId: string;
    subscriptionType?: string;
    subscriptionData: {
      subscriptionId: string;
      customerId: string;
      status: string;
      currentPeriodEnd: number;
      currentPeriodStart: number;
      subscriptionType?: string;
      paymentMethod: {
        last4: string;
        brand: string;
        expiryMonth: number;
        expiryYear: number;
      };
    };
  } | null;

  const [step, setStep] = useState<'account' | 'complete'>('account');

  const [formData, setFormData] = useState({
    name: '',
    email: paymentInfo?.email || '',
    password: '',
    confirmPassword: '',
    countryCode: '+65',
    phoneNumber: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<{
    checking?: boolean;
    available?: boolean;
    error?: string;
  }>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    sessionStorage.setItem('allow_unverified_setup', '1');
    return () => {
      sessionStorage.removeItem('allow_unverified_setup');
    };
  }, []);

  // Username validation (matches mobile UsernameService rules)
  const validateUsername = useCallback((username: string): string | null => {
    const trimmed = username.trim();
    if (!trimmed) return null; // empty = no feedback yet
    if (trimmed.length < 3) return 'Username must be at least 3 characters';
    if (trimmed.length > 20) return 'Username must be 20 characters or less';
    if (!/^[a-zA-Z0-9][a-zA-Z0-9_]{2,19}$/.test(trimmed)) {
      return 'Only letters, numbers, and underscores. Cannot start with underscore.';
    }
    return null;
  }, []);

  // Debounced availability check
  useEffect(() => {
    const username = formData.name.trim();
    const validationError = validateUsername(username);

    if (!username || username.length < 3 || validationError) {
      setUsernameStatus(validationError ? { error: validationError } : {});
      return;
    }

    setUsernameStatus({ checking: true });

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const checkFn = httpsCallable(functions, 'checkUsernameAvailability');
        const result = await checkFn({ username });
        const data = result.data as { available: boolean };
        setUsernameStatus({
          available: data.available,
          error: data.available ? undefined : 'Username is already taken',
        });
      } catch {
        setUsernameStatus({ error: 'Could not check availability' });
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [formData.name, validateUsername]);

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return false;
    }

    // Validate username format
    const usernameError = validateUsername(formData.name);
    if (usernameError) {
      setError(usernameError);
      return false;
    }

    // Check availability was confirmed
    if (!usernameStatus.available) {
      setError('Please choose an available username');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Validate phone number (must have at least 6 digits)
    const cleanedPhone = formData.phoneNumber.replace(/[\s\-()]/g, '');
    if (!cleanedPhone || cleanedPhone.length < 6) {
      setError('Please enter a valid phone number');
      return false;
    }

    return true;
  };

  const inferSubscriptionType = (priceId?: string): 'monthly' | 'yearly' => {
    const pid = (priceId || '').toLowerCase();
    if (pid.includes('annual') || pid.includes('year')) return 'yearly';
    return 'monthly';
  };

  const mapSubscriptionStatus = (
    stripeStatus?: string
  ): 'active' | 'inactive' | 'cancelled' | 'canceling' => {
    const s = (stripeStatus || '').toLowerCase();
    if (s === 'active' || s === 'trialing') return 'active';
    if (s === 'canceled' || s === 'cancelled') return 'cancelled';
    return 'inactive';
  };

  const handleCreateAccount = async () => {
    if (!validateForm()) return;

    setError('');
    setLoading(true);

    try {
      console.log('🚀 Creating Firebase Auth account...');
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;
      console.log('Auth account created:', user.uid);

      try {
        await updateProfile(user, { displayName: formData.name.trim() });
      } catch (e) {
        console.warn('Could not update auth displayName:', e);
      }

      console.log('📄 Creating Firestore document...');
      const userRef = doc(db, 'users', user.uid);

      const hasPayment = !!paymentInfo?.subscriptionData;

      const actualSubscriptionType =
        paymentInfo?.subscriptionType ||
        paymentInfo?.subscriptionData.subscriptionType ||
        inferSubscriptionType(paymentInfo?.priceId);

      console.log('💳 Subscription type:', actualSubscriptionType);

      // Build phone number in E.164 format
      const fullPhoneNumber = `${formData.countryCode}${formData.phoneNumber.replace(/[\s\-()]/g, '')}`;

      // Build Firestore data explicitly
      const usernameValue = formData.name.trim();
      const firestoreData: any = {
        displayName: usernameValue,
        email: formData.email,
        phoneNumberUnverified: fullPhoneNumber, // Will be verified on mobile
        role: 'user',
        createdAt: serverTimestamp(),
        smsFactorEnabled: false, // Will be set to true after mobile SMS verification
        // Initialize email preferences with defaults
        emailPreferences: {
          monthlyOffers: false,
          newsAndUpdates: false,
          rewardsReminders: false,
        },
      };

      // Add subscription data if payment exists
      if (hasPayment) {
        firestoreData.subscriptionStatus = mapSubscriptionStatus(
          paymentInfo!.subscriptionData.status
        );
        firestoreData.subscriptionType = actualSubscriptionType;
        firestoreData.stripePriceId = paymentInfo!.priceId;
        firestoreData.stripePaymentMethodId = paymentInfo!.paymentMethodId;
        firestoreData.stripeCustomerId =
          paymentInfo!.subscriptionData.customerId;
        firestoreData.stripeSubscriptionId =
          paymentInfo!.subscriptionData.subscriptionId;
        firestoreData.currentPeriodEnd = Timestamp.fromMillis(
          paymentInfo!.subscriptionData.currentPeriodEnd * 1000
        );
        firestoreData.currentPeriodStart = Timestamp.fromMillis(
          paymentInfo!.subscriptionData.currentPeriodStart * 1000
        );
        firestoreData.paymentMethodDetails =
          paymentInfo!.subscriptionData.paymentMethod;

        // Add nested subscription object for backward compatibility
        firestoreData.subscription = {
          subscriptionId: paymentInfo!.subscriptionData.subscriptionId,
          customerId: paymentInfo!.subscriptionData.customerId,
          priceId: paymentInfo!.priceId,
          status: paymentInfo!.subscriptionData.status,
          currentPeriodEnd: Timestamp.fromMillis(
            paymentInfo!.subscriptionData.currentPeriodEnd * 1000
          ),
          currentPeriodStart: Timestamp.fromMillis(
            paymentInfo!.subscriptionData.currentPeriodStart * 1000
          ),
          paymentMethod: paymentInfo!.subscriptionData.paymentMethod,
          createdAt: serverTimestamp(),
        };
      }

      console.log('🔍 About to save to Firestore:', firestoreData);
      console.log('🔍 subscriptionType value:', firestoreData.subscriptionType);

      await setDoc(userRef, firestoreData);
      console.log('✅ Firestore document created');

      // Update Stripe customer metadata to link to Firebase UID
      if (hasPayment && paymentInfo!.subscriptionData?.customerId) {
        try {
          console.log('🔗 Linking Stripe customer to Firebase user...');
          const updateMetadata = httpsCallable(
            functions,
            'updateStripeCustomerMetadata'
          );
          await updateMetadata({
            customerId: paymentInfo!.subscriptionData.customerId,
            firebaseUID: user.uid,
          });
          console.log('✅ Stripe customer linked to Firebase user');
        } catch (metadataError) {
          console.warn('⚠️ Could not update Stripe metadata:', metadataError);
          // Non-critical error, account creation still successful
        }
      }

      // Claim username via Cloud Function (usernameLookup is write-protected)
      const setUsernameFn = httpsCallable(functions, 'setUsername');
      await setUsernameFn({ username: usernameValue });
      console.log('✅ Username claimed via Cloud Function');

      // Send email verification
      await sendEmailVerification(user);
      console.log('📧 Email verification sent');

      // Sign out and complete
      await signOut(auth);
      sessionStorage.removeItem('allow_unverified_setup');
      console.log('✅ Account created successfully');
      setStep('complete');
    } catch (err: any) {
      console.error('Account creation error:', err);

      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else {
        setError(err.message || 'Failed to create account');
      }

      if (auth.currentUser) {
        try {
          console.log('🧹 Cleaning up failed account...');
          const userRef = doc(db, 'users', auth.currentUser.uid);
          await deleteDoc(userRef);
          await auth.currentUser.delete();
          console.log('Cleanup complete');
        } catch (cleanupErr) {
          console.error('Cleanup error:', cleanupErr);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate('/login', {
      state: {
        message:
          'Account created! Please check your email for a confirmation link to verify your account before logging in. If you cannot find it, check your spam or junk folder.',
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Account Creation Step */}
        {step === 'account' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Create Your Account
              </h1>
              <p className="text-gray-600">
                Complete your registration for Unveil
              </p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  disabled={loading}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    usernameStatus.error
                      ? 'border-red-300'
                      : usernameStatus.available
                        ? 'border-green-300'
                        : 'border-gray-300'
                  }`}
                  placeholder="e.g. johndoe"
                  autoCapitalize="off"
                  autoCorrect="off"
                />
                {usernameStatus.checking && (
                  <p className="text-xs text-gray-500 mt-1">
                    Checking availability...
                  </p>
                )}
                {usernameStatus.available && (
                  <p className="text-xs text-green-600 mt-1">
                    Username is available
                  </p>
                )}
                {usernameStatus.error && (
                  <p className="text-xs text-red-600 mt-1">
                    {usernameStatus.error}
                  </p>
                )}
                {!usernameStatus.checking &&
                  !usernameStatus.available &&
                  !usernameStatus.error &&
                  formData.name.trim().length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      3-20 characters, letters, numbers, and underscores only
                    </p>
                  )}
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  placeholder="you@example.com"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) =>
                      setFormData({ ...formData, countryCode: e.target.value })
                    }
                    disabled={loading}
                    className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  >
                    <option value="+65">+65 (SG)</option>
                    <option value="+60">+60 (MY)</option>
                    <option value="+1">+1 (US)</option>
                    <option value="+44">+44 (UK)</option>
                    <option value="+61">+61 (AU)</option>
                    <option value="+91">+91 (IN)</option>
                    <option value="+86">+86 (CN)</option>
                    <option value="+81">+81 (JP)</option>
                    <option value="+82">+82 (KR)</option>
                  </select>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, phoneNumber: e.target.value })
                    }
                    disabled={loading}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="91234567"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Required for mobile app verification
                </p>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {showPassword ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      )}
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  At least 8 characters
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        confirmPassword: e.target.value,
                      })
                    }
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {showConfirmPassword ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              <button
                onClick={handleCreateAccount}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>

            <div className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <a
                href="/login"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Sign in
              </a>
            </div>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <svg
                  className="w-10 h-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Account Created!
              </h2>
              <p className="text-gray-600">Email verification sent</p>
            </div>

            <button
              onClick={handleComplete}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors"
            >
              Continue to Login
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountPage;
